import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Braces, AlertCircle, Copy, Check } from 'lucide-react';
import { VariableDialog } from './variable-dialog';
import { Editor } from 'slate';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface ShortLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editor: Editor;
  shortLinkForm: ReturnType<typeof import('../hooks/use-shortlink').useShortLink>;
}

export function ShortLinkDialog({ open, onOpenChange, shortLinkForm }: ShortLinkDialogProps) {
  const {
    baseUrl,
    setBaseUrl,
    errors,
    parameters,
    editingParameterIndex,
    setEditingParameterIndex,
    hasCopied,
    addParameter,
    removeParameter,
    updateParameter,
    getHumanReadablePreview,
    handleSubmit,
    handleCopyUrl,
    captureSelection,
    isEditing
  } = shortLinkForm;

  useEffect(() => {
    if (open) {
      captureSelection();
    }
  }, [open, captureSelection]);

  const [showVariableDialog, setShowVariableDialog] = useState(false);

  const handleVariableSelect = (variableName: string, defaultValue: string | null) => {
    if (editingParameterIndex !== null) {
      const variableText = defaultValue
        ? `{{${variableName} | default: '${defaultValue}'}}`
        : `{{${variableName}}}`;
      updateParameter(editingParameterIndex, 'value', variableText);
    }
    setShowVariableDialog(false);
    setEditingParameterIndex(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[780px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Short Link' : 'Add Short Link with Tracking'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <div className="grid gap-2">
            <Label htmlFor="url">Link</Label>
            <Input
              id="url"
              placeholder="https://company.com/new-arrivals"
              value={baseUrl}
              onChange={(e) => {
                setBaseUrl(e.target.value);
              }}
              className={errors.url ? 'border-red-500' : ''}
            />
            {errors.url && (
              <div className="flex items-center gap-2 text-xs text-red-500">
                <AlertCircle className="h-4 w-4" />
                <span>{errors.url}</span>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Links should start with http:// or https://
            </p>
          </div>

          <div className="grid gap-4">
            <Label>UTM Parameters</Label>
            <div className="grid gap-4">
              {parameters.map((param, index) => (
                <div key={index} className="grid grid-cols-[1fr,1fr,auto] gap-4 items-start">
                  <div className="grid gap-1">
                    <Input
                      placeholder="Parameter"
                      value={param.name}
                      onChange={(e) => {
                        updateParameter(index, 'name', e.target.value);
                      }}
                      className={errors[`param${index}`] && !param.name ? 'border-red-500' : ''}
                    />
                    {errors[`param${index}`] && !param.name && (
                      <div className="flex items-center gap-2 text-xs text-red-500">
                        <AlertCircle className="h-3 w-3" />
                        <span>{errors[`param${index}`]}</span>
                      </div>
                    )}
                  </div>
                  <div className="grid gap-1">
                    <div className="relative">
                      <Input
                        placeholder="Value"
                        value={param.value}
                        className={`pr-8 ${errors[`param${index}`] && !param.value ? 'border-red-500' : ''}`}
                        onChange={(e) => {
                          updateParameter(index, 'value', e.target.value);
                        }}
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="absolute right-1 top-1/2 -translate-y-1/2"
                        onClick={() => {
                          setEditingParameterIndex(index);
                          setShowVariableDialog(true);
                        }}
                      >
                        <Braces className="h-4 w-4" />
                      </Button>
                    </div>
                    {errors[`param${index}`] && !param.value && (
                      <div className="flex items-center gap-2 text-xs text-red-500">
                        <span>{errors[`param${index}`]}</span>
                      </div>
                    )}
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="self-start mt-[2px]"
                    onClick={() => {
                      removeParameter(index);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <Button variant="outline" className="w-full" onClick={addParameter}>
              <Plus className="mr-2 h-4 w-4" />
              Add Parameter
            </Button>
          </div>

          <div className="grid gap-2">
            <Label>Preview</Label>
            <div className="relative">
              <pre
                className={cn(
                  'whitespace-pre-wrap rounded-md bg-muted p-4 text-sm min-h-[60px] flex items-center',
                  !getHumanReadablePreview() && 'text-muted-foreground italic'
                )}
              >
                {getHumanReadablePreview() || 'Enter a URL to see the preview'}
              </pre>
              {getHumanReadablePreview() && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-2"
                  onClick={() => {
                    void handleCopyUrl();
                  }}
                >
                  {hasCopied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </div>

          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const success = handleSubmit();
                if (success) {
                  onOpenChange(false);
                }
              }}
            >
              {isEditing ? 'Update Link' : 'Insert Link'}
            </Button>
          </div>
        </div>
      </DialogContent>

      <VariableDialog
        open={showVariableDialog}
        onOpenChange={setShowVariableDialog}
        onVariableSelected={handleVariableSelect}
        selectedVariable={null}
      />
    </Dialog>
  );
}
